import rules from './rules';
import registration from './registration';
import bridge from './bridge';
import { ILog } from '../interface/log';
import { IProgressPromiseGenerator } from '../interface/promise';
import { IServiceWorker, IServiceWorkerCallback, IServiceWorkerStatus } from '../interface/worker';
import { IFetcher, IFetchRequest } from '../interface/fetch';
import { IConfigCache } from '../interface/config';

const DEFAULT_EXPECTED_FILE_SIZE = 1024 & 512; /* 512KiB */

interface IServiceWorkerPrivate extends IServiceWorker {
    registration: ServiceWorkerRegistration | null;
    worker: ServiceWorker | null;
    statusChangeCallbacks: Array<IServiceWorkerCallback>;
}

function _requestInNamedCache(request: Request, cacheName: string): Promise<boolean> {
    return new Promise(function (resolve, reject) {
        caches
            .open(cacheName)
            .then(function (cache) {
                return cache.match(request, {
                    ignoreMethod: true,
                    ignoreSearch: true,
                });
            })
            .then(function (response) {
                const hasCachedValue = response !== undefined;
                resolve(hasCachedValue);
            })
            .catch(error => reject(error));
    });
}

const _notifyCallbacksOfStatusChange = (worker: IServiceWorkerPrivate) => {
    for (const callback of worker.statusChangeCallbacks) {
        callback(worker as IServiceWorker);
    }
};

function ServiceWorker(
    dependencies: {
        log: ILog;
        fetcher: IFetcher;
        promiser: IProgressPromiseGenerator;
        config: IConfigCache;
    },
    url: string,
    scope: string,
): IServiceWorker {
    const response: IServiceWorkerPrivate = {
        status: IServiceWorkerStatus.Idle,
        registration: null,
        worker: null,
        statusChangeCallbacks: [],

        isInterceptingNetworkRequests: function (this: IServiceWorkerPrivate) {
            return navigator?.serviceWorker?.controller !== null;
        },

        start: function (this: IServiceWorkerPrivate) {
            const _that = this;

            let hasInstalledRules = false;

            const _installRules = (worker: ServiceWorker) => {
                if (hasInstalledRules) {
                    return;
                }
                hasInstalledRules = true;

                rules(dependencies.log, worker, dependencies.config);
            };

            const _onReady = (worker: ServiceWorker) => {
                _that.worker = worker;

                _that.status = IServiceWorkerStatus.Ready;
                _notifyCallbacksOfStatusChange(_that);

                _installRules(worker);

                const checkControllerStatus = () => {
                    _that.status = _that.isInterceptingNetworkRequests()
                        ? IServiceWorkerStatus.Running
                        : IServiceWorkerStatus.Ready;
                    _notifyCallbacksOfStatusChange(_that);
                };

                navigator?.serviceWorker?.addEventListener(
                    'controllerchange',
                    checkControllerStatus,
                );

                checkControllerStatus();

                /* cleanup: unsubscribe & remove event listener when we are no longer in ready state */
                const statusChangeCb = (swCallback: IServiceWorker) => {
                    const shouldUnsub =
                        swCallback.status !== IServiceWorkerStatus.Ready &&
                        swCallback.status !== IServiceWorkerStatus.Running;

                    if (shouldUnsub) {
                        navigator?.serviceWorker?.removeEventListener(
                            'controllerchange',
                            checkControllerStatus,
                        );
                        _that.unsubscribeStatusChange(statusChangeCb);
                    }
                };

                _that.subscribeStatusChange(statusChangeCb);
            };

            const _onInstall = (worker: ServiceWorker) => {
                _installRules(worker);
                _that.worker = worker;
            };

            registration.start(dependencies.log, url, {
                scope,
                onInstall: _onInstall,
                onReady: _onReady,
                onRegister: reg => {
                    _that.registration = reg;
                },
            });

            dependencies.log.info('Starting service-worker');
            this.status = IServiceWorkerStatus.Starting;
            _notifyCallbacksOfStatusChange(this);
        },

        subscribeStatusChange: function (
            this: IServiceWorkerPrivate,
            callback: IServiceWorkerCallback,
        ) {
            this.statusChangeCallbacks.push(callback);
        },

        unsubscribeStatusChange: function (
            this: IServiceWorkerPrivate,
            callback: IServiceWorkerCallback,
        ) {
            this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
        },

        stop: function (this: IServiceWorkerPrivate) {
            if (!this.registration) {
                dependencies.log.error('Worker not registered, skipping stop');
                return;
            }
            this.status = IServiceWorkerStatus.Stopped;
            _notifyCallbacksOfStatusChange(this);

            registration.stop(this.registration);
        },

        addCacheFirst: function (
            this: IServiceWorkerPrivate,
            matcher: RegExp,
            maxAge: number,
            maxEntries: number,
            cacheName: string,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addCacheFirst');
                return;
            }
            bridge.addCacheFirst(this.worker, matcher, maxAge, maxEntries, cacheName);
        },

        addOAuthTokenCache: function (this: IServiceWorkerPrivate, pathSuffix: string) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addOAuthCache');
                return;
            }
            bridge.addOAuthTokenCache(this.worker, pathSuffix);
        },

        addZipPathFetchResponder: function (
            this: IServiceWorkerPrivate,
            zipPath: string,
            relativeRespondPath: string,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addZipPathFetchResponder');
                return;
            }
            bridge.addZipPathFetchResponder(this.worker, zipPath, relativeRespondPath);
        },

        addHeader: function (
            this: IServiceWorkerPrivate,
            matcher: RegExp,
            headerKey: string,
            headerVal: string,
            expiration: Date,
        ) {
            if (!this.worker) {
                dependencies.log.error('Worker not registered!, Skipping addHeader');
                return;
            }
            bridge.addHeader(this.worker, matcher, headerKey, headerVal, expiration);
        },

        isRequestCached: function (
            this: IServiceWorkerPrivate,
            request: IFetchRequest,
            cacheName?: string,
        ): Promise<boolean> {
            return new Promise<boolean>(async function (resolve, reject) {
                try {
                    if (window.caches === undefined) {
                        reject('No caches found');
                        return;
                    }

                    const cacheNames = [cacheName];
                    const reqCheck = new Request(request.url, {
                        method: request.method,
                        body: request.body,
                    });

                    const results = await Promise.all(
                        cacheNames.map(name => _requestInNamedCache(reqCheck, cacheName ?? '')),
                    );
                    const anyCacheHits = results.reduce((prev, cur) => prev || cur);

                    resolve(anyCacheHits);
                } catch (error) {
                    reject(error);
                }
            });
        },

        addRequestsToCache: function (
            this: IServiceWorkerPrivate,
            requests: Array<IFetchRequest>,
            cacheName: string,
        ) {
            return dependencies.promiser.promise<void>(async function (resolve, reject, progress) {
                try {
                    const fetches = requests.map(request => dependencies.fetcher(request));
                    const totalBytesToDownload = requests
                        .map(request => request.expectedResponseSize ?? DEFAULT_EXPECTED_FILE_SIZE)
                        .reduce((prev, cur) => prev + cur);
                    /* how much to assume each request factors into the progress updates */
                    const weightings = requests.map(
                        request =>
                            (request.expectedResponseSize ?? DEFAULT_EXPECTED_FILE_SIZE) /
                            totalBytesToDownload,
                    );

                    /* download each fetch in order sequentially */
                    for (let idx = 0; idx < fetches.length; idx++) {
                        const fetch = fetches[idx];
                        const progressUptoThisFetch = weightings
                            .map((val, i) => (i < idx ? val : 0))
                            .reduce((prev, cur) => prev + cur);
                        const progressWeighting =
                            weightings[
                                idx
                            ]; /* how much is this request worth overall to the progress */
                        fetch.onProgress(progressNow => {
                            const progressCb =
                                progressUptoThisFetch + progressNow * progressWeighting;
                            progress(progressCb);
                        });
                        const result = await fetch;

                        if (!result.isStatusOk) {
                            throw Error(
                                `Cache-Download failed for request: ${requests[idx].url}, error:${result.error}`,
                            );
                        }
                    }

                    resolve();
                } catch (error) {
                    reject(error);
                }
            });
        },
    };

    return response;
}

export default ServiceWorker;
